import routingService from 'common/src/modules/routing/routing.service';
import {RootState} from 'common/src/redux/store';
import {z} from 'zod';
import {ToolName} from '../../copilot.enums';
import {ToolExecuteParams, ToolResult} from '../../copilot.types';
import tool from '../../utils/creators/tool';
import toolResult from '../../utils/creators/toolResult';
import toolError from '../../utils/toolError';
import buildAppRoute from './utils/buildAppRoute';
import promptToRoute from './utils/promptToRoute';
import validateAppRoute from './utils/validateAppRoute';
import waitForNavigationCompletion from './utils/waitForNavigationCompletion';

const inputSchema = z.object({
  prompt: z.string().describe('Natural language navigation request'),
});

function generalNavigationTool(getState: () => RootState) {
  async function execute({
    input: {prompt},
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const promptToRouteResult = await promptToRoute(prompt, toolUtils, callChain);
      const {routeKey, pathParamValues, queryParamValues} = promptToRouteResult;

      if (!promptToRouteResult.success || !routeKey) {
        const {missingInfo} = promptToRouteResult;

        return toolError(
          `Could not navigate to the requested route. The following information was missing: ${missingInfo}`,
        );
      }

      const routeDef = routingService.getRouteDefinition(routeKey);

      const appRoute = buildAppRoute({
        routeDef,
        pathParamValues,
        queryParamValues,
        logger: toolUtils.logger,
      });

      const errorMessage = validateAppRoute({appRoute, routeDef});

      if (errorMessage) {
        return toolError(`Error resolving route: ${errorMessage}`);
      }

      routingService.goTo(appRoute);

      const navigationResult = await waitForNavigationCompletion({
        getState,
        isNavigationComplete: routeDef.isNavigationComplete ?? (() => true),
        logger: toolUtils.logger,
      });

      const url = routingService.createUrl(appRoute);

      if (!navigationResult.passed) {
        return toolError(`Navigation failed: ${navigationResult.reason}`);
      }

      return toolResult(`Successfully navigated to ${url}`);
    } catch (error) {
      return toolError(
        `Navigation failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
      );
    }
  }

  return tool({
    name: ToolName.GeneralNavigation,
    displayName: 'Navigating',
    description: 'Navigate to pages and routes using natural language',
    execute,
    inputSchema,
  });
}

export default generalNavigationTool;
